import { useState } from 'react';
import { RCAMethod } from '@cbm/shared';
import { 
  HelpCircle, 
  Plus, 
  Minus, 
  Save, 
  FileText,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

interface RCAAnalysisFormProps {
  ncrId: string;
  onSubmit: (analysis: RCAAnalysis) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface RCAAnalysis {
  method: RCAMethod;
  analysis: any;
}

interface FiveWhyStep {
  question: string;
  answer: string;
  evidence?: string;
}

interface FishboneCategory {
  name: string;
  causes: string[];
}

interface FaultTreeNode {
  id: string;
  description: string;
  type: 'event' | 'gate' | 'basic';
  children?: FaultTreeNode[];
  probability?: number;
}

export function RCAAnalysisForm({ ncrId, onSubmit, onCancel, loading = false }: RCAAnalysisFormProps) {
  const [method, setMethod] = useState<RCAMethod>('5-why');
  const [fiveWhySteps, setFiveWhySteps] = useState<FiveWhyStep[]>([
    { question: 'Why did the problem occur?', answer: '', evidence: '' }
  ]);
  const [fishboneCategories, setFishboneCategories] = useState<FishboneCategory[]>([
    { name: 'Machine', causes: [''] },
    { name: 'Method', causes: [''] },
    { name: 'Material', causes: [''] },
    { name: 'Manpower', causes: [''] },
    { name: 'Measurement', causes: [''] },
    { name: 'Environment', causes: [''] }
  ]);
  const [faultTreeRoot, setFaultTreeRoot] = useState<FaultTreeNode>({
    id: 'root',
    description: 'Top Event',
    type: 'event',
    children: []
  });
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['root']));

  const handleSubmit = () => {
    let analysis: any;

    switch (method) {
      case '5-why':
        analysis = {
          steps: fiveWhySteps.filter(step => step.answer.trim() !== ''),
          rootCause: fiveWhySteps[fiveWhySteps.length - 1]?.answer || '',
        };
        break;
      case 'fishbone':
        analysis = {
          categories: fishboneCategories.map(cat => ({
            ...cat,
            causes: cat.causes.filter(cause => cause.trim() !== '')
          })).filter(cat => cat.causes.length > 0),
        };
        break;
      case 'fault-tree':
        analysis = {
          tree: faultTreeRoot,
        };
        break;
    }

    onSubmit({ method, analysis });
  };

  const addFiveWhyStep = () => {
    const nextStep = fiveWhySteps.length + 1;
    setFiveWhySteps([
      ...fiveWhySteps,
      { 
        question: `Why did "${fiveWhySteps[fiveWhySteps.length - 1]?.answer || 'the previous cause'}" occur?`, 
        answer: '', 
        evidence: '' 
      }
    ]);
  };

  const removeFiveWhyStep = (index: number) => {
    if (fiveWhySteps.length > 1) {
      setFiveWhySteps(fiveWhySteps.filter((_, i) => i !== index));
    }
  };

  const updateFiveWhyStep = (index: number, field: keyof FiveWhyStep, value: string) => {
    const updated = [...fiveWhySteps];
    updated[index] = { ...updated[index], [field]: value };
    setFiveWhySteps(updated);
  };

  const addFishboneCause = (categoryIndex: number) => {
    const updated = [...fishboneCategories];
    updated[categoryIndex].causes.push('');
    setFishboneCategories(updated);
  };

  const removeFishboneCause = (categoryIndex: number, causeIndex: number) => {
    const updated = [...fishboneCategories];
    if (updated[categoryIndex].causes.length > 1) {
      updated[categoryIndex].causes.splice(causeIndex, 1);
      setFishboneCategories(updated);
    }
  };

  const updateFishboneCause = (categoryIndex: number, causeIndex: number, value: string) => {
    const updated = [...fishboneCategories];
    updated[categoryIndex].causes[causeIndex] = value;
    setFishboneCategories(updated);
  };

  const toggleNodeExpansion = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderFaultTreeNode = (node: FaultTreeNode, depth = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.id} className="space-y-2">
        <div 
          className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50"
          style={{ marginLeft: `${depth * 20}px` }}
        >
          {hasChildren && (
            <button onClick={() => toggleNodeExpansion(node.id)}>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
          <div className={`
            w-3 h-3 rounded-full
            ${node.type === 'event' ? 'bg-red-500' : 
              node.type === 'gate' ? 'bg-blue-500' : 'bg-green-500'}
          `} />
          <span className="text-sm">{node.description}</span>
          {node.probability && (
            <span className="text-xs text-gray-500">P={node.probability}</span>
          )}
        </div>
        
        {isExpanded && hasChildren && (
          <div className="space-y-2">
            {node.children!.map(child => renderFaultTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Root Cause Analysis</h2>
          <p className="text-sm text-gray-600">NCR: {ncrId}</p>
        </div>
        <div className="flex items-center space-x-2">
          <HelpCircle className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-500">Choose analysis method</span>
        </div>
      </div>

      {/* Method Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Analysis Method
        </label>
        <div className="flex space-x-4">
          {(['5-why', 'fishbone', 'fault-tree'] as RCAMethod[]).map((methodOption) => (
            <label key={methodOption} className="flex items-center">
              <input
                type="radio"
                name="method"
                value={methodOption}
                checked={method === methodOption}
                onChange={(e) => setMethod(e.target.value as RCAMethod)}
                className="mr-2"
              />
              <span className="text-sm capitalize">
                {methodOption === '5-why' ? '5-Why Analysis' :
                 methodOption === 'fishbone' ? 'Fishbone Diagram' :
                 'Fault Tree Analysis'}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Method-specific forms */}
      <div className="mb-6">
        {method === '5-why' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">5-Why Analysis</h3>
              <button
                onClick={addFiveWhyStep}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Add Why</span>
              </button>
            </div>
            
            {fiveWhySteps.map((step, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Why #{index + 1}</h4>
                  {fiveWhySteps.length > 1 && (
                    <button
                      onClick={() => removeFiveWhyStep(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      value={step.question}
                      onChange={(e) => updateFiveWhyStep(index, 'question', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Why did this occur?"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Answer
                    </label>
                    <textarea
                      value={step.answer}
                      onChange={(e) => updateFiveWhyStep(index, 'answer', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Describe the cause..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Evidence (Optional)
                    </label>
                    <input
                      type="text"
                      value={step.evidence || ''}
                      onChange={(e) => updateFiveWhyStep(index, 'evidence', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Supporting evidence or data..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {method === 'fishbone' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Fishbone Diagram</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fishboneCategories.map((category, categoryIndex) => (
                <div key={category.name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{category.name}</h4>
                    <button
                      onClick={() => addFishboneCause(categoryIndex)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {category.causes.map((cause, causeIndex) => (
                      <div key={causeIndex} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={cause}
                          onChange={(e) => updateFishboneCause(categoryIndex, causeIndex, e.target.value)}
                          className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Potential cause..."
                        />
                        {category.causes.length > 1 && (
                          <button
                            onClick={() => removeFishboneCause(categoryIndex, causeIndex)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {method === 'fault-tree' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Fault Tree Analysis</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-4">
                Build a fault tree by defining events and their relationships. 
                This is a simplified interface - in production, you'd have a more sophisticated tree builder.
              </p>
              
              <div className="space-y-2">
                {renderFaultTreeNode(faultTreeRoot)}
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded">
                <p className="text-sm text-blue-700">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Fault tree analysis requires specialized tools. This interface shows the concept - 
                  in practice, you'd use dedicated FTA software.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Submitting...' : 'Submit RCA'}</span>
        </button>
      </div>
    </div>
  );
}